import axios, {
  AxiosHeaders,
  CanceledError,
  type AxiosAdapter,
  type AxiosRequestConfig,
} from 'axios'

import { FalhaHttp } from './falha-http'
import type {
  ClienteHttp,
  EstrategiaRetry,
  HeadersHttp,
  MetodoHttp,
  OpcoesHttp,
  ParametrosHttp,
  RespostaHttp,
} from './http.tipos'
import { normalizarFalhaHttp } from './normalizar-falha-http'

export type ConfiguracaoClienteHttp = Readonly<{
  servico: string
  urlBase: string
  urlFallback?: string
  timeoutMs: number
}>

export type DependenciasInternasHttp = Readonly<{
  adapter?: AxiosAdapter
  esperar?: EsperarRetry
}>

export type EsperarRetry = (atrasoMs: number, signal?: AbortSignal) => Promise<void>

export type RequisicaoHttp = Readonly<{
  metodo: MetodoHttp
  rota: string
  urlBase: string
  corpo?: unknown
  headers?: HeadersHttp
  parametros?: ParametrosHttp
  timeoutMs: number
  signal?: AbortSignal
}>

const rotaEhAbsoluta = (rota: string): boolean => (
  rota.startsWith('//') || /^[a-z][a-z\d+.-]*:/i.test(rota)
)

const normalizarEndpoint = (rota: string): string => rota.split(/[?#]/, 1)[0] ?? rota

const normalizarHeaders = (headers: unknown): Record<string, string> => {
  const resultado: Record<string, string> = {}
  const valores: Record<string, unknown> = headers instanceof AxiosHeaders
    ? headers.toJSON()
    : typeof headers === 'object' && headers !== null
      ? headers as Record<string, unknown>
      : {}

  for (const [nome, valor] of Object.entries(valores)) {
    if (valor === null) continue
    if (Array.isArray(valor)) {
      resultado[nome] = valor
        .filter((item): item is string => typeof item === 'string')
        .join(', ')
    } else if (
      typeof valor === 'string'
      || typeof valor === 'number'
      || typeof valor === 'boolean'
    ) {
      resultado[nome] = String(valor)
    }
  }

  return resultado
}

const obterRequestId = (headers: HeadersHttp): string | undefined => (
  headers['x-request-id'] ?? headers['x-correlation-id']
)

const esperarComCancelamento: EsperarRetry = (atrasoMs, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(new CanceledError('Retry cancelado'))
    return
  }

  const cancelar = () => {
    clearTimeout(timer)
    reject(new CanceledError('Retry cancelado'))
  }
  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', cancelar)
    resolve()
  }, atrasoMs)

  signal?.addEventListener('abort', cancelar, { once: true })
})

export abstract class ClienteHttpBase implements ClienteHttp {
  private readonly instanciaAxios
  private readonly esperar: EsperarRetry

  protected constructor(
    protected readonly configuracao: ConfiguracaoClienteHttp,
    dependenciasInternas: DependenciasInternasHttp = {},
  ) {
    this.instanciaAxios = axios.create({
      baseURL: configuracao.urlBase,
      timeout: configuracao.timeoutMs,
      ...(dependenciasInternas.adapter ? { adapter: dependenciasInternas.adapter } : {}),
    })
    this.esperar = dependenciasInternas.esperar ?? esperarComCancelamento
  }

  get<T>(rota: string, opcoes?: OpcoesHttp): Promise<RespostaHttp<T>> {
    return this.executar<T>('GET', rota, undefined, opcoes)
  }

  post<TResposta>(
    rota: string,
    corpo?: unknown,
    opcoes?: OpcoesHttp,
  ): Promise<RespostaHttp<TResposta>> {
    return this.executar<TResposta>('POST', rota, corpo, opcoes)
  }

  put<TResposta>(
    rota: string,
    corpo?: unknown,
    opcoes?: OpcoesHttp,
  ): Promise<RespostaHttp<TResposta>> {
    return this.executar<TResposta>('PUT', rota, corpo, opcoes)
  }

  patch<TResposta>(
    rota: string,
    corpo?: unknown,
    opcoes?: OpcoesHttp,
  ): Promise<RespostaHttp<TResposta>> {
    return this.executar<TResposta>('PATCH', rota, corpo, opcoes)
  }

  delete<T>(rota: string, opcoes?: OpcoesHttp): Promise<RespostaHttp<T>> {
    return this.executar<T>('DELETE', rota, undefined, opcoes)
  }

  protected prepararRequisicao(
    requisicao: RequisicaoHttp,
  ): RequisicaoHttp | Promise<RequisicaoHttp> {
    return requisicao
  }

  private async executar<T>(
    metodo: MetodoHttp,
    rota: string,
    corpo?: unknown,
    opcoes?: OpcoesHttp,
  ): Promise<RespostaHttp<T>> {
    if (rotaEhAbsoluta(rota)) {
      throw new TypeError('A rota HTTP deve ser relativa ao backend configurado')
    }

    const totalTentativas = 1 + (opcoes?.retry?.tentativas.length ?? 0)
    let urlBase = this.configuracao.urlBase

    for (
      let numeroTentativa = 1;
      numeroTentativa <= totalTentativas;
      numeroTentativa += 1
    ) {
      try {
        return await this.executarTentativa<T>(metodo, rota, corpo, opcoes, urlBase)
      } catch (erro: unknown) {
        const falha = erro instanceof FalhaHttp
          ? erro
          : normalizarFalhaHttp(erro, {
              servico: this.configuracao.servico,
              metodo,
              endpoint: normalizarEndpoint(rota),
            })
        const tentativaRetry = opcoes?.retry?.tentativas[numeroTentativa - 1]

        if (
          !tentativaRetry
          || !this.deveRepetir(falha, opcoes.retry, numeroTentativa, totalTentativas)
        ) {
          throw falha
        }

        try {
          await this.esperar(tentativaRetry.atrasoMs, opcoes.signal)
        } catch (erroEspera: unknown) {
          throw normalizarFalhaHttp(erroEspera, {
            servico: this.configuracao.servico,
            metodo,
            endpoint: normalizarEndpoint(rota),
          })
        }

        urlBase = tentativaRetry.destino === 'fallback'
          ? this.configuracao.urlFallback ?? this.configuracao.urlBase
          : this.configuracao.urlBase
      }
    }

    throw new TypeError('Estrategia de retry invalida')
  }

  private deveRepetir(
    falha: FalhaHttp,
    estrategia: EstrategiaRetry,
    numeroTentativa: number,
    totalTentativas: number,
  ): boolean {
    if (falha.tipo === 'cancelamento') return false
    if (estrategia.deveRepetir) {
      return estrategia.deveRepetir(falha, { numeroTentativa, totalTentativas })
    }
    return falha.tipo === 'rede'
      || falha.tipo === 'timeout'
      || (falha.tipo === 'http' && falha.status !== undefined && falha.status >= 500)
  }

  private async executarTentativa<T>(
    metodo: MetodoHttp,
    rota: string,
    corpo: unknown,
    opcoes: OpcoesHttp | undefined,
    urlBase: string,
  ): Promise<RespostaHttp<T>> {
    const requisicaoBase: RequisicaoHttp = {
      metodo,
      rota,
      urlBase,
      timeoutMs: opcoes?.timeoutMs ?? this.configuracao.timeoutMs,
      ...(corpo === undefined ? {} : { corpo }),
      ...(opcoes?.headers ? { headers: opcoes.headers } : {}),
      ...(opcoes?.parametros ? { parametros: opcoes.parametros } : {}),
      ...(opcoes?.signal ? { signal: opcoes.signal } : {}),
    }

    try {
      const requisicao = await this.prepararRequisicao(requisicaoBase)
      const config: AxiosRequestConfig = {
        method: requisicao.metodo,
        url: requisicao.rota,
        baseURL: requisicao.urlBase,
        timeout: requisicao.timeoutMs,
        ...(requisicao.corpo === undefined ? {} : { data: requisicao.corpo }),
        ...(requisicao.headers ? { headers: requisicao.headers } : {}),
        ...(requisicao.parametros ? { params: requisicao.parametros } : {}),
        ...(requisicao.signal ? { signal: requisicao.signal } : {}),
      }
      const resposta = await this.instanciaAxios.request<T>(config)
      const headers = normalizarHeaders(resposta.headers)
      const requestId = obterRequestId(headers)

      return {
        data: resposta.data,
        status: resposta.status,
        headers,
        ...(requestId ? { requestId } : {}),
      }
    } catch (erro: unknown) {
      throw normalizarFalhaHttp(erro, {
        servico: this.configuracao.servico,
        metodo,
        endpoint: normalizarEndpoint(rota),
      })
    }
  }
}
